'use client';

import {Button as ButtonPrimitive} from '@base-ui/react/button';
import {useDirection} from '@base-ui/react/direction-provider';
import {Popover as PopoverPrimitive} from '@base-ui/react/popover';
import {cva} from 'class-variance-authority';
import {Check, ChevronDown} from 'lucide-react';
import {useState, type MouseEvent, type ReactNode} from 'react';

import {cn} from '../../lib/utils';
import {OptionTag} from '../MultiSelect/OptionTag';

const multiSelectVariantsConfig = {
  variants: {},
  defaultVariants: {}
} as const;

const multiSelectVariants = cva(
  'flex min-h-[42px] w-full flex-wrap items-center gap-1 rounded-md border border-border bg-background-surface px-3 py-[7px] text-base shadow-xs transition-[color,box-shadow] outline-none field-focus field-error disabled:cursor-not-allowed disabled:opacity-50',
  multiSelectVariantsConfig
);

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onValueChange?: (values: string[]) => void;
  placeholder?: string;
  removeLabel?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  badge?: ReactNode;
  'aria-invalid'?: boolean;
}

function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder,
  removeLabel = 'Remove',
  disabled = false,
  className,
  id,
  badge,
  'aria-invalid': ariaInvalid
}: MultiSelectProps) {
  const [internalValue, setInternalValue] = useState<string[]>([]);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : internalValue;
  const direction = useDirection();

  function handleToggle(optionValue: string) {
    const next = selected.includes(optionValue)
      ? selected.filter(v => v !== optionValue)
      : [...selected, optionValue];
    if (!isControlled) {
      setInternalValue(next);
    }
    onValueChange?.(next);
  }

  function handleRemove(optionValue: string, e: MouseEvent) {
    e.stopPropagation();
    const next = selected.filter(v => v !== optionValue);
    if (!isControlled) {
      setInternalValue(next);
    }
    onValueChange?.(next);
  }

  const selectedLabels = selected
    .map(v => options.find(opt => opt.value === v))
    .filter((opt): opt is MultiSelectOption => !!opt);

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger
        data-slot="multi-select"
        id={id}
        disabled={disabled}
        aria-invalid={ariaInvalid || undefined}
        className={cn(multiSelectVariants(), className)}
      >
        <span className="flex flex-1 flex-wrap items-center gap-1">
          {selectedLabels.length === 0 ? (
            <span className="text-fg-tertiary">{placeholder}</span>
          ) : (
            selectedLabels.map(opt => (
              <OptionTag
                key={opt.value}
                label={opt.label}
                removeLabel={removeLabel}
                onRemove={e => handleRemove(opt.value, e)}
              />
            ))
          )}
        </span>
        {badge}
        <ChevronDown className="size-4 shrink-0 text-fg-tertiary" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          side="bottom"
          sideOffset={4}
          align="start"
          className="isolate z-50"
          dir={direction}
        >
          <PopoverPrimitive.Popup
            data-slot="multi-select-content"
            className={cn(
              'z-50 w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-hidden rounded-md border border-border bg-background-surface text-fg shadow-md duration-100',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95'
            )}
          >
            <div className="p-1">
              {options.map(opt => {
                const isSelected = selected.includes(opt.value);
                return (
                  <ButtonPrimitive
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleToggle(opt.value)}
                    className={cn(
                      'flex w-full cursor-default items-center justify-between rounded-sm px-3 py-2 text-sm text-fg outline-none select-none',
                      'hover:bg-background-surface_hover focus:bg-background-surface_hover'
                    )}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <Check className="size-4 shrink-0 text-fg" />}
                  </ButtonPrimitive>
                );
              })}
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export {MultiSelect, multiSelectVariants, multiSelectVariantsConfig};
export type {MultiSelectProps};
